import getRuntimeEnvironment from '@signal/utils/env/getRuntimeEnvironment'
import { UserDetailsType } from '../user/types'
import InstrumentationError from './Error'
import {
  ACCOUNT_GLOBAL,
  MAX_EVENT_PARAMETERS,
  TELEM_FUNC_NORMAL,
  TELEM_PARAM_ACCOUNT_ID,
  TELEM_PARAM_APP_NAME,
  TELEM_PARAM_ENVIRONMENT,
  TELEM_PARAM_USER_ID,
  TELEM_PARAM_USER_TYPE,
  USER_TYPE_INTERNAL,
  USER_TYPE_EXTERNAL
} from './constants'
import { TelemetryPayloadType } from './types'
import { validateEventName } from './utils'

type TelemetryParamsType = {
  activeAccountId?: string
  appName?: string
  eventName?: string
  eventPayload?: TelemetryPayloadType
  pagePayload?: TelemetryPayloadType
  telemetryFunc?: string
  userDetails: UserDetailsType
}

/**
 * This function generates the telemetry payload used with all events, comprising the app name, account id,
 * user details, and per-page payload provided by the underying app, if any
 * @param {Object} telemetryParams - telemetry parameters
 */
function generateTelemetryPayload({
  activeAccountId,
  appName,
  pagePayload = {},
  userDetails
}: TelemetryParamsType): TelemetryPayloadType {
  const { id, isPureCarsEmployee = false } = userDetails
  return {
    [TELEM_PARAM_ACCOUNT_ID]: activeAccountId || ACCOUNT_GLOBAL,
    [TELEM_PARAM_APP_NAME]: appName,
    [TELEM_PARAM_ENVIRONMENT]: getRuntimeEnvironment(),
    [TELEM_PARAM_USER_TYPE]: isPureCarsEmployee
      ? USER_TYPE_INTERNAL
      : USER_TYPE_EXTERNAL,
    [TELEM_PARAM_USER_ID]: id,
    ...pagePayload
  } as TelemetryPayloadType
}

/**
 * This function can be used to issue an app instrumentation page view
 * @param {Object} telemetryParams - telemetry parameters
 */
export function sendTelemetryPageView(
  telemetryParams: TelemetryParamsType
): void {
  const {
    telemetryFunc = TELEM_FUNC_NORMAL,
    userDetails: { isImpersonating }
  } = telemetryParams

  if (isImpersonating) {
    // don't send analytics if impersonating
    return
  }

  const telemetryPayload = generateTelemetryPayload(telemetryParams)

  // @ts-ignore
  if (window[telemetryFunc]) {
    // @ts-ignore
    window[telemetryFunc]('event', 'page_view', telemetryPayload)
  }
}

/**
 * This function can be used to issue an app instrumentation analytics call
 * @param {Object} telemetryParams - telemetry parameters
 */
export function sendTelemetryEvent(telemetryParams: TelemetryParamsType): void {
  const {
    eventName,
    eventPayload = {},
    telemetryFunc = TELEM_FUNC_NORMAL,
    userDetails
  } = telemetryParams

  if (!userDetails || userDetails.isImpersonating) {
    // don't send analytics if impersonating
    return
  }

  const error = validateEventName(eventName)
  if (error) {
    throw new InstrumentationError(error)
  }

  const telemetryPayload = {
    ...generateTelemetryPayload(telemetryParams),
    ...eventPayload
  }

  if (Object.keys(telemetryPayload).length > MAX_EVENT_PARAMETERS) {
    throw new InstrumentationError(
      'tried to send telemetry with an unsupported number of payload parameters'
    )
  }

  // @ts-ignore
  if (window[telemetryFunc]) {
    // @ts-ignore
    window[telemetryFunc]('event', eventName, telemetryPayload)
  }
}

export default sendTelemetryEvent
